import { setOptions, importLibrary } from "@googlemaps/js-api-loader";
import { useEffect, useRef, useCallback, useState } from "react";

export default function MapSection({
  locations,
  setLocations,
  iframeWidth,
  hideReview,
  isMobile,
  isOther,
}) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const directionsRendererRef = useRef(null);
  const routeDataRef = useRef(null);

  const [mapReady, setMapReady] = useState(false);

  const validLocations = locations.filter(
    (loc) => typeof loc.lat === "number" && typeof loc.lng === "number",
  );

  const routeKey = validLocations
    .map((loc) => `${loc.lat},${loc.lng}`)
    .join("|");

  const clearMarkers = useCallback(() => {
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
  }, []);

  // Place Markers
  const placeMarkers = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!map || validLocations.length === 0) return;

    clearMarkers();
    const bounds = new google.maps.LatLngBounds();

    validLocations.forEach((loc) => {
      const marker = new google.maps.Marker({
        position: { lat: loc.lat, lng: loc.lng },
        map,
      });
      markersRef.current.push(marker);
      bounds.extend(marker.getPosition());
    });

    if (validLocations.length === 1) {
      map.setCenter(bounds.getCenter());
      map.setZoom(isOther ? 9 : 14);
    } else {
      const leftPadding = isMobile ? 40 : hideReview ? 100 : iframeWidth + 100;

      map.fitBounds(bounds, {
        top: 100,
        bottom: 100,
        left: leftPadding,
        right: 100,
      });
    }
  }, [
    validLocations,
    clearMarkers,
    isMobile,
    iframeWidth,
    hideReview,
    isOther,
  ]);

  // Draw Routes
  const drawRoutes = useCallback(() => {
    if (
      !mapInstanceRef.current ||
      !directionsRendererRef.current ||
      validLocations.length < 2
    ) {
      return;
    }

    const directionsService = new google.maps.DirectionsService();

    const waypoints = validLocations.slice(1, -1).map((loc) => ({
      location: { lat: loc.lat, lng: loc.lng },
      stopover: true,
    }));

    directionsService.route(
      {
        origin: { lat: validLocations[0].lat, lng: validLocations[0].lng },
        destination: {
          lat: validLocations[validLocations.length - 1].lat,
          lng: validLocations[validLocations.length - 1].lng,
        },
        waypoints,
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (response, status) => {
        if (status !== "OK" || !response) return;

        routeDataRef.current = response;
        directionsRendererRef.current.setDirections(response);

        const legs = response.routes[0].legs;

        setLocations((prev) =>
          prev.map((loc, i) => {
            if (i > 0 && legs[i - 1]) {
              return {
                ...loc,
                travelDurationFromPrevious: legs[i - 1].duration.value,
              };
            }

            return loc;
          }),
        );
      },
    );
  }, [validLocations, setLocations]);

  // Initialize map once
  useEffect(() => {
    let isMounted = true;
    async function init() {
      if (!mapRef.current) return;
      setOptions({
        key: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
        version: "weekly",
      });

      const { Map, RenderingType } = await importLibrary("maps");
      await importLibrary("routes");
      await importLibrary("marker");

      if (!isMounted || !mapRef.current) return;

      const map = new Map(mapRef.current, {
        center: { lat: 39.8283, lng: -98.5795 },
        zoom: 5,
        renderingType: RenderingType.VECTOR,
        disableDefaultUI: isMobile,
      });

      mapInstanceRef.current = map;

      directionsRendererRef.current = new google.maps.DirectionsRenderer({
        map,
        suppressMarkers: true,
        preserveViewport: true,
      });

      setMapReady(true);
    }

    init();

    return () => {
      isMounted = false;
    };
  }, [isMobile]);

  useEffect(() => {
    if (!mapReady) return;

    placeMarkers();

    if (validLocations.length > 1) {
      drawRoutes();
    }
  }, [mapReady, routeKey]);

  return (
    <div
      ref={mapRef}
      style={{
        width: "100%",
        height: "100%",
        minHeight: isMobile ? "300px" : "100%",
      }}
    />
  );
}
